import { StrokeType, TextDecoration, TextScript, TextTransform } from '@bynder-studio/misc';
import {
    type BoundedTextProps,
    type IStructuredGlyph,
    getStructuredText,
    HorizontalAlignment,
    Placement,
    type StyleProps,
    type TextProps,
    TextRenderer,
    VerticalAlignment,
} from '@bynder-studio/structured-text';
import { ElementUpdateTypes } from '../../Enums/ElementUpdateTypes';
import { BaseProperty } from './BaseProperty';
import { type SubtitleCue, type SubtitlesElementParams } from '../../types';
import { GroupCompElement } from '../CompModels/Elements/GroupCompElement';
import { SvgCompElement } from '../CompModels/Elements/SvgCompElement';
import { Box } from '../Shared/Box';
import { Position } from '../Shared/Position';
import { Dimension } from '../Shared/Dimension';

// ── FFmpeg / libass default SRT→ASS conversion constants ──
// When FFmpeg converts SRT to ASS internally, it uses these PlayRes values
// and style defaults. All ASS coordinates are in PlayRes units and scale
// to actual video pixels via: px = value × (videoDimension / playRes).
const LIBASS_PLAY_RES_X = 384;
const LIBASS_PLAY_RES_Y = 288;
const LIBASS_FONT_SIZE = 16; // PlayRes units
const LIBASS_OUTLINE = 1; // PlayRes units  (BorderStyle=1)
const LIBASS_SHADOW = 0; // PlayRes units
const LIBASS_MARGIN_L = 20; // PlayRes units
const LIBASS_MARGIN_R = 20; // PlayRes units
const LIBASS_MARGIN_V = 20; // PlayRes units
// Alignment 2 = bottom-center (SSA numpad convention)

/** Primary fill colour – white, fully opaque */
const LIBASS_PRIMARY_COLOR = {
    type: 'SOLID' as const,
    red: 255,
    green: 255,
    blue: 255,
    opacity: 1,
    brandColorId: null,
};

/** Outline colour – black, fully opaque */
const LIBASS_OUTLINE_COLOR = { type: 'SOLID' as const, red: 0, green: 0, blue: 0, opacity: 1, brandColorId: null };

/**
 * Compute pixel-accurate libass subtitle metrics from the actual video size.
 *
 * font_size_px   = LIBASS_FONT_SIZE × (videoHeight / LIBASS_PLAY_RES_Y)
 * outline_px     = LIBASS_OUTLINE   × (videoHeight / LIBASS_PLAY_RES_Y)
 * marginL_px     = LIBASS_MARGIN_L  × (videoWidth  / LIBASS_PLAY_RES_X)
 * marginR_px     = LIBASS_MARGIN_R  × (videoWidth  / LIBASS_PLAY_RES_X)
 * marginV_px     = LIBASS_MARGIN_V  × (videoHeight / LIBASS_PLAY_RES_Y)
 * maxLineWidth   = videoWidth - marginL_px - marginR_px
 */
function computeLibassMetrics(videoWidth: number, videoHeight: number) {
    const scaleX = videoWidth / LIBASS_PLAY_RES_X;
    const scaleY = videoHeight / LIBASS_PLAY_RES_Y;

    return {
        fontSize: LIBASS_FONT_SIZE * scaleY,
        outline: LIBASS_OUTLINE * scaleY,
        shadow: LIBASS_SHADOW * scaleY,
        marginL: LIBASS_MARGIN_L * scaleX,
        marginR: LIBASS_MARGIN_R * scaleX,
        marginV: LIBASS_MARGIN_V * scaleY,
        maxLineWidth: videoWidth - LIBASS_MARGIN_L * scaleX - LIBASS_MARGIN_R * scaleX,
    };
}

/**
 * Build the StyleProps that match libass default SRT rendering for the given
 * video dimensions.  Font size and outline thickness are derived from the
 * standard PlayRes scaling formulas.
 */
function buildLibassStyleProps(videoWidth: number, videoHeight: number): StyleProps {
    const metrics = computeLibassMetrics(videoWidth, videoHeight);

    return {
        fontId: 'default',
        fontSize: metrics.fontSize,
        styleId: null,
        color: LIBASS_PRIMARY_COLOR,
        tracking: 0,
        leading: 1.0, // libass uses the font's natural line height, no extra leading
        textTransform: TextTransform.NONE,
        textDecoration: TextDecoration.NONE,
        textScript: TextScript.BASE,
        stroke: {
            type: StrokeType.CENTER,
            width: metrics.outline,
        },
        paragraphSpacing: 0,
    };
}

export const defaultSubtitlesElementParams: SubtitlesElementParams = {
    id: Date.now() - 777 ** 3,
    name: 'DEFAULT_SUBTITLES_NAME',
    locked: false,
    enabled: true,
    cues: [],
    formattedText: null,
    opacity: 100,
    rotation: 0,
    dimension: new Dimension(0, 0),
    position: new Position(0, 0),
};

export class SubtitlesElement extends BaseProperty {
    enabled = true;

    cues: SubtitleCue[] = [];

    formattedText: TextProps | null = null;

    readonly opacity = 100;

    readonly rotation = 0;

    dimension: Dimension = new Dimension(0, 0);

    position: Position = new Position(0, 0);

    private _cachedCueId: string | null = null;

    private _cachedCompElements: GroupCompElement[] | null = null;

    /** Persisted slot assignments for libass-style collision detection.
     *  Slot 0 = bottom-most position, slot 1 = one row above, etc.
     *  Existing cues keep their slot; new cues take the lowest free slot. */
    private _slotMap: Map<string, number> = new Map();

    constructor(params: SubtitlesElementParams) {
        super();
        this.setProperties(params);
    }

    update(params: Partial<SubtitlesElementParams>): Set<ElementUpdateTypes> {
        const updateTypes = this.setProperties(params);

        // Invalidate cache when cues or enabled state changes
        if (params.cues !== undefined || params.enabled !== undefined) {
            this._cachedCueId = null;
            this._cachedCompElements = null;
            this.formattedText = null;
        }

        return updateTypes;
    }

    setProperties(params: Partial<SubtitlesElementParams>): Set<ElementUpdateTypes> {
        const updateTypes: Set<ElementUpdateTypes> = super.setProperties(params);

        if (params.enabled !== undefined) {
            this.enabled = params.enabled;
        }

        if (params.cues !== undefined) {
            this.cues = params.cues;
        }

        if (params.formattedText !== undefined) {
            this.formattedText = params.formattedText;
        }

        if (params.position !== undefined && params.position !== null) {
            this.position = new Position(params.position.x, params.position.y);
            updateTypes.add(ElementUpdateTypes.POSITION);
        }

        if (params.dimension !== undefined && params.dimension !== null) {
            this.dimension = new Dimension(params.dimension.width, params.dimension.height);
            updateTypes.add(ElementUpdateTypes.DIMENSION);
        }

        return updateTypes;
    }

    getActiveCues(frameIndex: number): SubtitleCue[] {
        if (!this.enabled) {
            return [];
        }

        return this.cues.filter((cue) => frameIndex >= cue.startFrame && frameIndex < cue.endFrame);
    }

    private buildFormattedText(cueText: string, videoWidth: number, videoHeight: number): TextProps {
        const styleProps = buildLibassStyleProps(videoWidth, videoHeight);

        return getStructuredText(cueText, styleProps);
    }

    getCompElements(frameIndex: number, canvasDimension: Dimension): GroupCompElement[] {
        // TODO: Will be set during the parsing! Delete after full integration
        this.dimension = canvasDimension.getCopy();

        const activeCues = this.getActiveCues(frameIndex);

        if (activeCues.length === 0) {
            this.formattedText = null;

            return [];
        }

        // Build a cache key from the sorted set of active cue IDs
        const cacheKey = activeCues.map((c) => c.id).join('|');

        if (this._cachedCueId === cacheKey && this._cachedCompElements) {
            this._cachedCompElements.forEach((el) => {
                el.originalElement = this as any;
            });

            return this._cachedCompElements;
        }

        const canvasWidth = canvasDimension.getWidth();
        const canvasHeight = canvasDimension.getHeight();

        // libass collision detection: existing cues keep their slot,
        // new cues take the lowest available slot.
        const activeCueIds = new Set(activeCues.map((c) => c.id));

        // Remove slots for cues that are no longer active
        for (const cueId of this._slotMap.keys()) {
            if (!activeCueIds.has(cueId)) {
                this._slotMap.delete(cueId);
            }
        }

        // Assign slots for new cues (lowest free slot)
        const usedSlots = new Set(this._slotMap.values());

        for (const cue of activeCues) {
            if (!this._slotMap.has(cue.id)) {
                let slot = 0;

                while (usedSlots.has(slot)) {
                    slot++;
                }

                this._slotMap.set(cue.id, slot);
                usedSlots.add(slot);
            }
        }

        // Order cues by slot (highest slot = topmost line, slot 0 = bottom line)
        const cuesBySlot = activeCues
            .map((cue) => ({ cue, slot: this._slotMap.get(cue.id) ?? 0 }))
            .sort((a, b) => b.slot - a.slot);

        // Combine all cue texts into a single string — each cue becomes a line
        const combinedText = cuesBySlot.map(({ cue }) => cue.text).join('\n');
        const combinedId = cuesBySlot.map(({ cue }) => cue.id).join('_');

        this.formattedText = this.buildFormattedText(combinedText, canvasWidth, canvasHeight);

        const group = this.buildCompElement(combinedId, canvasDimension);
        const compElements = [group];

        this._cachedCompElements = compElements;
        this._cachedCueId = cacheKey;

        return compElements;
    }

    /**
     * libass WrapStyle=0 ("smart wrapping") — balance line widths.
     *
     * Algorithm (mirrors ass_split_lines in libass):
     *  1. Shape with full maxLineWidth → greedy line count N.
     *  2. If N ≤ 1, nothing to balance → return as-is.
     *  3. Binary-search for the narrowest width that still wraps into
     *     exactly N lines.  The result is a set of lines whose widths
     *     are approximately equal, with the top line slightly wider.
     */
    private createSmartWrappedBoundedText(textRenderer: TextRenderer, maxWidth: number, boxHeight: number) {
        const makeBounded = (width: number) => {
            const config: BoundedTextProps = {
                textStruct: this.formattedText,
                boxWidth: width,
                boxHeight,
                placement: Placement.WRAP,
                horizontalAlignment: HorizontalAlignment.CENTER,
                verticalAlignment: VerticalAlignment.BOTTOM,
            };

            return textRenderer.createBoundedText(this.formattedText, config);
        };

        const countLines = (bt: ReturnType<typeof makeBounded>) => bt.getShapedText().getShapedLines().length;

        // Step 1 – greedy wrap at full width
        const greedyBounded = makeBounded(maxWidth);
        const targetLines = countLines(greedyBounded);

        if (targetLines <= 1) {
            return greedyBounded;
        }

        // Step 2 – binary search for the narrowest width that still fits in targetLines
        let narrowest = 0;
        let widest = maxWidth;
        let bestBounded = greedyBounded;

        // ~20 iterations gives sub-pixel precision for any realistic resolution
        for (let i = 0; i < 20; i++) {
            const mid = (narrowest + widest) / 2;
            const candidate = makeBounded(mid);

            if (countLines(candidate) <= targetLines) {
                // Still fits in targetLines → try narrower
                bestBounded = candidate;
                widest = mid;
            } else {
                // Too narrow → need wider
                narrowest = mid;
            }
        }

        return bestBounded;
    }

    private buildCompElement(cueId: string, canvasDimension: Dimension): GroupCompElement {
        const canvasWidth = canvasDimension.getWidth();
        const canvasHeight = canvasDimension.getHeight();
        const metrics = computeLibassMetrics(canvasWidth, canvasHeight);

        // Use TextRenderer to shape the subtitle text, same as TextElement.init() + constructCompElement()
        const textRenderer = TextRenderer.getInstance();

        // libass Alignment=2 (bottom-center): text wraps within the margin area
        // maxLineWidth = videoWidth - marginL_px - marginR_px
        const boxWidth = metrics.maxLineWidth;
        const boxHeight = canvasHeight - metrics.marginV;

        // libass WrapStyle=0: smart-wrap to balance line widths
        const boundedText = this.createSmartWrappedBoundedText(textRenderer, boxWidth, boxHeight);
        const shapedLines = boundedText.getShapedText().getShapedLines();

        // The smart-wrap may have narrowed the BoundedText box to balance line widths.
        // Glyphs are centered within that (possibly narrower) box.
        // To center them on the full canvas, offset by:
        //   marginL + (fullBoxWidth - actualBoxWidth) / 2
        const [actualBoxWidth] = boundedText.getDimension();
        const boxX = metrics.marginL + (boxWidth - actualBoxWidth) / 2;
        const boxY = 0;

        // Build glyph SvgCompElements — same pattern as TextElement.getSvgCompElement
        const compLines: GroupCompElement[] = [];
        let charIdx = 0;

        shapedLines.forEach((line, lineIdx) => {
            const lineChars: SvgCompElement[] = [];

            line.glyphs.forEach((glyph: IStructuredGlyph) => {
                // Skip newline glyphs — they have no visible path and their degenerate
                // bounding boxes break GroupCompElement position calculations.
                if (glyph.isNewLine() || glyph.data.path === '') {
                    return;
                }

                const compEl = new SvgCompElement();
                compEl.id = `${this.id}_sub_${lineIdx}_${charIdx}`;

                const color = boundedText.getShapedText().getTextPropsProperty('color', glyph.data.runIdx);
                compEl.color = color;

                compEl.path = glyph.data.path;
                compEl.decorationPaths = glyph.data.decorationPaths;
                compEl.horizontalPathScale = glyph.data.pathScale;
                compEl.verticalPathScale = -1 * glyph.data.pathScale;
                compEl.renderOrder = 99999;
                // libass visually shows only the outer half of the outline (the fill covers the inner half),
                // so we halve it to match the same visual thickness.
                compEl.strokeWidth = metrics.outline / 2;
                compEl.strokeType = StrokeType.OUTSIDE;
                compEl.strokeColor = LIBASS_OUTLINE_COLOR;
                compEl.fillColor = color;
                compEl.glyph = glyph.data;

                const absContentBbox = glyph.data.absContentBbox;

                compEl.contentBox = new Box(
                    new Position(0, 0),
                    new Dimension(absContentBbox.width, absContentBbox.height),
                );
                compEl.pathPosition = new Position(
                    glyph.data.absLeftPx - absContentBbox.left,
                    glyph.data.absTopPx - absContentBbox.top,
                );
                compEl.boundingBox = new Box(
                    new Position(boxX + absContentBbox.left, boxY + absContentBbox.top),
                    new Dimension(absContentBbox.width, absContentBbox.height),
                );

                lineChars.push(compEl);
                charIdx++;
            });

            if (lineChars.length > 0) {
                const compLine = new GroupCompElement(lineChars);
                compLine.id = `${this.id}_sub_line_${lineIdx}`;
                compLine.name = `subtitle_line_${lineIdx}`;
                compLine.renderOrder = 99999;
                compLines.push(compLine);
            }
        });

        // Wrap all lines into a text group
        const textGroup = new GroupCompElement(compLines);
        textGroup.id = `${this.id}_sub_text_${cueId}`;
        textGroup.name = `subtitle_text_${cueId}`;
        textGroup.renderOrder = 99999;

        // Final group — use default makeChildrenRelative=true so positions cascade correctly.
        // Do NOT override boundingBox/contentBox; let GroupCompElement compute them from children.
        const group = new GroupCompElement([textGroup]);
        group.id = `${this.id}_subtitle_group_${cueId}`;
        group.renderOrder = 99999;
        group.opacity = this.opacity;
        group.rotation = this.rotation;
        group.originalElement = this as any;

        return group;
    }

    getValuesByUpcomingUpdate(data: Partial<SubtitlesElementParams>): Partial<SubtitlesElementParams> {
        return super.getValuesByUpcomingUpdate(data);
    }

    toObject(): SubtitlesElementParams {
        return {
            ...super.toObject(),
            enabled: this.enabled,
            cues: this.cues.map((cue) => ({ ...cue })),
            formattedText: this.formattedText,
            opacity: this.opacity,
            rotation: this.rotation,
            position: this.position?.toObject() ?? null,
            dimension: this.dimension?.toObject() ?? null,
        };
    }
}
